import React from 'react';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ROUTESPATH } from '@/routes';
import { QuoteStatus } from '../../types';

interface AvailableActions {
  canMarkAsFollowUp: boolean;
  canConvertToOrder: boolean;
}

interface QuoteQuickActionsProps {
  quoteId: string;
  currentStatus: number;
  availableActions: AvailableActions;
  isShowHeader: boolean;
  isDataLoading: boolean;
  hasLineItems: boolean;
  isSendingEmail: boolean;
  isDownloadingOrderPDF: boolean;
  canConvertQuoteToOrder: boolean;
  canManageQuotesStatus: boolean;
  canManageFollowup: boolean;
  onFollowUp?: () => void;
  onCompleteFollowUp?: () => void;
  onSendToCustomer: () => void;
  onConvertToOrder?: () => void;
  onDownloadPDF: () => void;
}

export const QuoteQuickActions: React.FC<QuoteQuickActionsProps> = ({
  quoteId,
  currentStatus,
  availableActions,
  isShowHeader,
  isDataLoading,
  hasLineItems,
  isSendingEmail,
  isDownloadingOrderPDF,
  canConvertQuoteToOrder,
  canManageQuotesStatus,
  canManageFollowup,
  onFollowUp,
  onCompleteFollowUp,
  onSendToCustomer,
  onConvertToOrder,
  onDownloadPDF,
}) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-lg p-6">
      <h3 className="text-xl text-left font-bold text-gray-900 mb-4">Quick Actions</h3>
      <div className="grid grid-cols-2 gap-3">
        {!isShowHeader && (
          <Button
            type="primary"
            className="h-12 text-sm font-medium"
            block
            onClick={() => navigate(ROUTESPATH.QUOTE_DETAIL.replace(':id', quoteId))}
          >
            View Full Quote
          </Button>
        )}

        {availableActions.canMarkAsFollowUp && canManageQuotesStatus && (
          <Button
            className="h-12 text-sm font-medium bg-blue-500 hover:bg-blue-600 text-white border-blue-500"
            onClick={onFollowUp}
            block
          >
            Follow Up
          </Button>
        )}

        {currentStatus === QuoteStatus.FOLLOW_UP_NEEDED && canManageFollowup && (
          <Button
            className="h-12 text-sm font-medium bg-purple-500 hover:bg-purple-600 text-white border-purple-500"
            onClick={() => onCompleteFollowUp?.()}
            block
          >
            Complete Follow-up
          </Button>
        )}

        {currentStatus !== QuoteStatus.DRAFT && (
          <Button
            className="h-12 text-sm font-medium"
            onClick={onSendToCustomer}
            loading={isSendingEmail}
            disabled={isDataLoading || !hasLineItems}
            block
          >
            {isSendingEmail ? 'Sending...' : 'Send to Customer'}
          </Button>
        )}

        {availableActions.canConvertToOrder && canConvertQuoteToOrder && (
          <Button
            type="primary"
            className="h-12 text-sm font-medium bg-green-500 hover:bg-green-600 border-green-500"
            onClick={onConvertToOrder}
            block
          >
            Place Order
          </Button>
        )}


        {currentStatus !== QuoteStatus.DRAFT && (
          <Button
            className="h-12 text-sm font-medium"
            onClick={onDownloadPDF}
            loading={isDownloadingOrderPDF}
            disabled={isDataLoading || !hasLineItems}
            block
          >
            {isDownloadingOrderPDF ? 'Generating PDF...' : 'Download PDF'}
          </Button>
        )}
      </div>
    </div>
  );
};
